import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { getExclusiveDealEndpointIdentifier } from './use-get-exclusive-deal-api';
import { ExclusiveDealResponseDto, getExclusiveDealsEndpointIdentifier } from './use-get-exclusive-deals-api';

const endpoint = 'ums/v1/cms/exclusive-deals';
export const updateDealProductCategoryEndpointIdentifier = `${endpoint}/:id/product-category`;

interface BodyType {
  catalogCategoryId: number;
}

interface ResponseData {
  data: ExclusiveDealResponseDto;
}

type TData = ResponseData;
type TError = AxiosError;

type TVariables = {
  dealId: number;
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useUpdateDealProductCategoryApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const mutationFn = async ({ dealId, body }: TVariables): Promise<TData> => {
    try {
      const { data } = await http.put(`${endpoint}/${dealId}/product-category`, body);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  return useMutationWrapper(mutationFn, {
    mutationKey: [updateDealProductCategoryEndpointIdentifier],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([getExclusiveDealsEndpointIdentifier]);
      queryClient.invalidateQueries([getExclusiveDealEndpointIdentifier, variables.dealId]);

      toast({
        description: 'Product category updated successfully',
        status: 'success',
      });

      if (userOnSuccess) {
        userOnSuccess(data, variables, context);
      }
    },
    ...restOptions,
  });
}
